<template>
  <!-- Damaged Package -->
  <DamagedPackage
    v-if="state.matches('reportProblemFlow.detailsStep.damagedPackage')"
    :isLoading
    :mostUrgentTask
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblemWithMessage('DAMAGED_PACKAGE', values)"
  />

  <!-- Out Of WarrantyItem -->
  <OutOfWarrantyItem
    v-if="state.matches('reportProblemFlow.detailsStep.oowItem')"
    :isLoading
    :mostUrgentTask
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblemWithMessage('OOW_ITEM', values)"
  />

  <!-- Item Not Received -->
  <ItemNotReceived
    v-if="state.matches('reportProblemFlow.detailsStep.itemNotReceived')"
    :isLoading
    :mostUrgentTask
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblemWithMessage('ITEM_NOT_RECEIVED', values)"
  />

  <!-- Account Locked -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.accountLocked')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.accountLocked)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblemWithMessage('ACCOUNT_LOCKED', values)"
  />

  <!-- Code Locked -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.codeLocked')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.codeLocked)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblemWithMessage('CODE_LOCKED', values)"
  />

  <!-- Missing Part -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.missingPart')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.missingPart)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblemWithMessage('MISSING_PART', values)"
  />

  <!-- Wrong Item -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.wrongItem')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.wrongItem)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblemWithMessage('WRONG_ITEM', values)"
  />

  <!-- Problem Not Identified -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.problemNotIdentified')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.problemNotIdentified)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="
      (values) => declareProblemWithMessage('PROBLEM_NOT_IDENTIFIED', values)
    "
  />

  <!-- Other -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.other')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.other)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblemWithMessage('OTHER', values)"
  />
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { handleFileUpload } from '@backmarket/front-upload'
import { getHttpBaseUrl } from '@backmarket/nuxt-module-http/getHttpBaseUrl'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import type { ProductReturnState } from '~/api/care-folders/types/care-folder'
import type {
  OrderlineOpenTask,
  ProblemType,
} from '~/api/seller-after-sales/types'
import { ROUTE_NAMES } from '~/constants/routes'
import { fetchAndGoToNextTask } from '~/scopes/customer-care/components/request.helpers'
import { useFormatDeadline } from '~/scopes/customer-care/pages/customer-care/composables/useFormatDeadline'

import type { MachineSend, MachineState } from '../../../ActionBar.machine.type'

import DamagedPackage from './DamagedPackage/DamagedPackage.vue'
import translations from './DetailsStep.translations'
import ItemNotReceived from './ItemNotReceived/ItemNotReceived.vue'
import OutOfWarrantyItem from './OutOfWarrantyItem/OutOfWarrantyItem.vue'
import ExplainProblem from './components/ExplainProblem/ExplainProblem.vue'

const { customerRequestId, mostUrgentTask, state, productReturnState } =
  defineProps<{
    send: MachineSend
    state: MachineState
    customerRequestId: number
    mostUrgentTask?: OrderlineOpenTask
    productReturnState: ProductReturnState
  }>()

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const { openErrorToast, openSuccessToast } = useTheToast()
const logger = useLogger()
const tracking = useTracking()
const isLoading = ref(false)

const emit = defineEmits(['done'])

const trackingEventName: Record<ProblemType, string> = {
  DAMAGED_PACKAGE: 'upload-evidence',
  OOW_ITEM: 'upload-evidence',
  ITEM_NOT_RECEIVED: 'upload-evidence',
  ACCOUNT_LOCKED: 'send-message',
  CODE_LOCKED: 'send-message',
  MISSING_PART: 'send-message',
  WRONG_ITEM: 'send-message',
  PROBLEM_NOT_IDENTIFIED: 'send-message',
  OTHER: 'send-message',
}

function handleTracking(problemType: ProblemType, isNextTaskChecked: boolean) {
  tracking.trackFormSubmit({
    name: trackingEventName[problemType],
    zone: 'asp-action-bar-report-problem-step',
    pageType: ROUTE_NAMES.CUSTOMER_CARE.CUSTOMER_REQUEST,
    customerRequestId,
    productReturnState,
    mostUrgentDeadline: mostUrgentTask
      ? useFormatDeadline(mostUrgentTask.deadline, mostUrgentTask.id)
      : '',
    mostUrgentTaskType: mostUrgentTask?.status || '',
    problemType,
    goToNextTask: isNextTaskChecked,
  })
}

async function getUploadedFilesHandles(files: File[]) {
  const filesPromises = files.map((file) =>
    handleFileUpload(
      `${getHttpBaseUrl()}/services/uploads`,
      'care_asp_attachment',
      { careFolderId: customerRequestId },
      file,
    ),
  )

  const uploadResponses = await Promise.all(filesPromises)
  const isUploadSuccessful = uploadResponses.every(
    (res) => res?.status === 'success',
  )

  if (!isUploadSuccessful) {
    throw new Error('Some attachments failed to upload')
  }

  return uploadResponses.map((res) => res?.handle)
}

async function declareProblemWithMessage(
  problemType: ProblemType,
  values: { message: string; attachments: File[]; isNextTaskChecked: boolean },
) {
  try {
    isLoading.value = true

    const attachmentHandles = await getUploadedFilesHandles(values.attachments)

    await $httpFetch(
      '/bm/customer_request/:customerRequestId/merchant/problem',
      {
        method: 'POST',
        pathParams: {
          customerRequestId,
        },
        body: {
          message: values.message,
          attachmentHandles,
          problem: problemType,
        },
      },
    )

    if (values.isNextTaskChecked)
      fetchAndGoToNextTask(mostUrgentTask?.id).catch(() => {
        openErrorToast()
      })

    handleTracking(problemType, values.isNextTaskChecked)
    emit('done')
    openSuccessToast()
  } catch (err) {
    openErrorToast()

    const error = err as Error
    logger.error(error.message, {
      error,
      owners: ['bot-squad-seller-care-front'],
    })
  } finally {
    isLoading.value = false
  }
}
</script>
